import type { StoredCV } from "@/lib/indexed-db";

export type SubmitCvReviewMessages = {
  uploadFailed: string;
  cvResponseMissing: string;
  reviewRequestFailed: string;
  reviewRequestMissing: string;
};

export type SubmitCvReviewStep = "upload" | "review";

export class SubmitCvReviewError extends Error {
  readonly status: number | undefined;
  readonly step: SubmitCvReviewStep;
  /**
   * Set when the CV was already uploaded before the failure. A retry must pass
   * it back so the file is not uploaded (and stored) a second time.
   */
  readonly cvId: string | undefined;

  constructor(
    message: string,
    status?: number,
    step: SubmitCvReviewStep = "upload",
    cvId?: string,
  ) {
    super(message);
    this.name = "SubmitCvReviewError";
    this.status = status;
    this.step = step;
    this.cvId = cvId;
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const getStringProperty = (
  value: unknown,
  property: string,
): string | undefined => {
  if (!isRecord(value)) {
    return undefined;
  }

  const propertyValue = value[property];
  return typeof propertyValue === "string" ? propertyValue : undefined;
};

const getResponseError = async (
  response: Response,
  fallback: string,
): Promise<string> => {
  try {
    const data: unknown = await response.json();
    return getStringProperty(data, "error") ?? fallback;
  } catch {
    return fallback;
  }
};

export type SubmitCvReviewOptions = {
  /**
   * Reuse a CV that a previous attempt already uploaded, so a retry only
   * repeats the step that actually failed.
   */
  cvId?: string;
};

async function uploadCv(
  payload: StoredCV,
  messages: SubmitCvReviewMessages,
): Promise<string> {
  const formData = new FormData();
  formData.append("file", payload.file);
  formData.append("experienceLevel", payload.experienceLevel);
  formData.append("reviewPurpose", payload.reviewPurpose);
  formData.append("targetJobTitle", payload.targetJobTitle);
  formData.append("programCampus", payload.programCampus);
  formData.append("jobDescription", payload.jobDescription);
  formData.append(
    "allowTalentPool",
    payload.allowTalentPool ? "true" : "false",
  );
  if (payload.reviewLanguage) {
    formData.append("reviewLanguage", payload.reviewLanguage);
  }
  if (payload.talentPoolUploadChoice) {
    formData.append("talentPoolUploadChoice", payload.talentPoolUploadChoice);
  }

  const cvResponse = await fetch("/api/cv", {
    method: "POST",
    body: formData,
  });

  if (!cvResponse.ok) {
    throw new SubmitCvReviewError(
      await getResponseError(cvResponse, messages.uploadFailed),
      cvResponse.status,
    );
  }

  const cvData: unknown = await cvResponse.json();
  const cvId = getStringProperty(cvData, "id");

  if (!cvId) {
    throw new SubmitCvReviewError(messages.cvResponseMissing);
  }

  return cvId;
}

export async function submitCvForReview(
  payload: StoredCV,
  messages: SubmitCvReviewMessages,
  options: SubmitCvReviewOptions = {},
): Promise<{ reviewRequestId: string }> {
  const cvId = options.cvId ?? (await uploadCv(payload, messages));

  const reviewResponse = await fetch("/api/review", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ cvId }),
  });

  if (!reviewResponse.ok) {
    throw new SubmitCvReviewError(
      await getResponseError(reviewResponse, messages.reviewRequestFailed),
      reviewResponse.status,
      "review",
      cvId,
    );
  }

  const reviewData: unknown = await reviewResponse.json();
  const reviewRequest = isRecord(reviewData)
    ? reviewData.reviewRequest
    : undefined;
  const reviewRequestId =
    getStringProperty(reviewRequest, "id") ??
    getStringProperty(reviewData, "id");

  if (!reviewRequestId) {
    throw new SubmitCvReviewError(
      messages.reviewRequestMissing,
      undefined,
      "review",
      cvId,
    );
  }

  return { reviewRequestId };
}
